import {
    createActionFactory,
    Feature,
    HubPersona,
    FavoritePerson,
    createThunk,
    getMatchingFavorite,
    PersonType,
    ThunkDispatch,
    FavoriteEntity,
    PrivateDistributionList,
    FavoritePrivateDistributionList,
    Person,
    createMapOfSet,
    getDisplayNameForFavoriting,
    UnifiedGroup,
    FavoriteUnifiedGroup,
} from '../../../shared';
import { addFavoritePersona, removeFavoritePersona } from '../data/favoriteAddRemoveDataActions';
import {
    getPackageState,
    getActiveLeftNavItemId,
    AppState,
    getAllHubPersonasAcrossFolders,
} from '../../../appstate';
import { HubState, packageStateKey } from '../..';
import { getDisplayedItemHubPersonaIds } from '../../selectors/personaListSelector';

export type { FavoritePerson };

export type AddFavoritePersonaClickedOrigin =
    | 'commandBar'
    | 'favoritePicker'
    | 'linkContacts'
    | 'hubList';

export const addFavoritePersonaClickedAction = createActionFactory<{
    origin: AddFavoritePersonaClickedOrigin;
    displayedHubPersonaIds: string[];
    activeLeftNavItemId: string;
    favorite: FavoriteEntity;
}>('addFavoritePersonaClickedAction', Feature.Favorite, action => ({
    origin: action.payload.origin,
}));
export const removeFavoritePersonaClickedAction = createActionFactory<{
    hubPersonaId: string;
    displayedHubPersonaIds: string[];
    activeLeftNavItemId: string;
    favoriteId: string;
    isGroup: boolean;
}>('removeFavoritePersonaClickedAction', Feature.Favorite);

export const addFavoritePersonaClicked = (
    persona: HubPersona,
    origin: AddFavoritePersonaClickedOrigin
) =>
    createThunk('AddFavoritePersonaClicked', (dispatch: ThunkDispatch, getState: () => {}) => {
        const state = getPackageState<HubState>(getState(), packageStateKey);

        if (state.appState.data.hubState.favoritePersonsState.isFavoritingInProgress) {
            // This is fix for people who are double clicking on slow machines
            // In happy case scenario the button is disabled after clicking on it
            return;
        }

        const isCAPIv3Enabled = state.appState.view.layoutState.isCAPIv3Enabled;

        let favorite: FavoriteEntity;
        if (persona.type === PersonType.Person) {
            favorite = createNewFavoritePerson(persona, isCAPIv3Enabled);
            // Don't use an invalid personaId (E.g.: recipient cache contacts), the email will be used to resolve the contact
            if (!isContactPersonaId(state.appState, persona.personaId)) {
                favorite = { ...favorite, personaId: undefined };
            }
        } else if (persona.type === PersonType.PrivateDistributionList) {
            favorite = createNewFavoritePdl(persona);
        } else if (persona.type === PersonType.Group) {
            favorite = createNewFavoriteUnifiedGroup(persona);
        } else {
            throw new Error('AddFavoritePersona type not supported');
        }

        const displayedHubPersonaIds = getDisplayedItemHubPersonaIds(
            state.appState,
            state.packageState
        );
        const activeLeftNavItemId = getActiveLeftNavItemId(state.appState);

        dispatch(
            addFavoritePersonaClickedAction({
                origin,
                displayedHubPersonaIds,
                activeLeftNavItemId,
                favorite,
            })
        );
        dispatch(addFavoritePersona(favorite));
    });

export const removeFavoritePersonaClicked = (persona: HubPersona) =>
    createThunk('RemoveFavoritePersonaClicked', (dispatch: ThunkDispatch, getState: () => {}) => {
        const state = getPackageState<HubState>(getState(), packageStateKey);

        if (state.appState.data.hubState.favoritePersonsState.isFavoritingInProgress) {
            // This is fix for people who are double clicking on slow machines
            // In happy case scenario the button is disabled after clicking on it
            return;
        }

        const displayedHubPersonaIds = getDisplayedItemHubPersonaIds(
            state.appState,
            state.packageState
        );
        const activeLeftNavItemId = getActiveLeftNavItemId(state.appState);

        // We can't be sure whether we have a Persona or a FavoritePerson here, so make sure we are operating with a FavoriteNode
        let hubPersonaId;
        let favoriteId;
        let displayName;
        let isGroup: boolean =
            persona.type === PersonType.Group ||
            persona.type === PersonType.GroupBasicData ||
            persona.type === PersonType.FavoriteUnifiedGroup;

        switch (persona.type) {
            case PersonType.Person:
            case PersonType.PrivateDistributionList:
            case PersonType.GroupBasicData:
            case PersonType.Group:
                const matchingFavorite = getMatchingFavorite(
                    persona,
                    state.appState.data.hubState.favoritePersonsState.favoritePersons
                );
                favoriteId = matchingFavorite.favoriteId;
                hubPersonaId = persona.hubPersonaId;
                displayName = matchingFavorite.info.displayName;
                break;

            case PersonType.FavoritePerson:
            case PersonType.FavoritePrivateDistributionList:
            case PersonType.FavoriteUnifiedGroup:
                hubPersonaId = persona.hubPersonaId;
                favoriteId = persona.favoriteId;
                displayName = persona.info.displayName;
                break;
            default:
                throw new Error('RemoveFavoritePersonaClicked type not supported');
        }

        // If no FavoriteId was found, do nothing
        if (!favoriteId) {
            return;
        }

        dispatch(
            removeFavoritePersonaClickedAction({
                hubPersonaId,
                displayedHubPersonaIds,
                activeLeftNavItemId,
                favoriteId,
                isGroup,
            })
        );
        dispatch(removeFavoritePersona(hubPersonaId, favoriteId, displayName, persona.type));
    });

function createNewFavoritePerson(
    person: Person,
    isCAPIv3Enabled: boolean | undefined
): FavoritePerson {
    return {
        type: PersonType.FavoritePerson,
        info: {
            allEmailAddresses: person.info.allEmailAddresses,
            displayName: getDisplayNameForFavoriting(person),
            mainEmailAddress: person.info.mainEmailAddress,
        },
        personaId: person.personaId,

        // for CAPIv3 contacts it inspects two contacts when a contact is marked as favorite hence creating a different hubPersonaId for it
        // this fix will not break anything as the hubPersonaId set here is temporary and will be replaced by a different hubPersonaID
        // for favorites when the page is refreshed and the data is fetched through Favorite API
        hubPersonaId: isCAPIv3Enabled ? `HubFavoriteId_${person.personaId}` : person.hubPersonaId,
        favoriteId: null,
        relevancyScore: person.relevancyScore,
    };
}

function createNewFavoritePdl(pdl: PrivateDistributionList): FavoritePrivateDistributionList {
    return {
        type: PersonType.FavoritePrivateDistributionList,
        hubPersonaId: pdl.hubPersonaId,
        favoriteId: null,
        info: {
            itemId: pdl.itemId,
            displayName: pdl.info.listOriginalDisplayName,
            members: pdl.members,
        },
    };
}

function createNewFavoriteUnifiedGroup(unifiedGroup: UnifiedGroup): FavoriteUnifiedGroup {
    return {
        type: PersonType.FavoriteUnifiedGroup,
        favoriteId: null,
        hubPersonaId: unifiedGroup.hubPersonaId,
        info: {
            displayName: unifiedGroup.displayName,
            emailAddress: unifiedGroup.emailAddress,
            externalDirectoryObjectId: unifiedGroup.externalDirectoryObjectId,
        },
    };
}

/**
 * Checks whether the given personaId is found in any contact stored in any folder
 * @param appState the application state
 * @param personaId the personaId to search
 */
function isContactPersonaId(appState: AppState, personaId: string): boolean {
    if (!personaId) {
        return false;
    }

    const allContactFoldersPersonas = getAllHubPersonasAcrossFolders(appState);
    const personaIdsInFolder = createMapOfSet(
        allContactFoldersPersonas.map(
            hubPersona =>
                hubPersona.type !== PersonType.FavoritePrivateDistributionList &&
                hubPersona.type !== PersonType.FavoriteUnifiedGroup &&
                hubPersona.personaId
        )
    );

    return !!personaIdsInFolder[personaId];
}
export type { Action } from '../../../shared';
